import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
import SectionHeader from "../../components/landing/SectionHeader";
import api from "../../services/api";
import { projectsApi } from "../../services/api";

const formatNaira = (amount) => `₦${Number(amount || 0).toLocaleString("en-NG")}`;

const CheckIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const PRODUCT_META = {
  idea: {
    eyebrow: "Starter",
    description: "Explore course-aware topic ideas before committing to a full project.",
  },
  proposal: {
    eyebrow: "Early stage",
    description: "A complete project proposal structured for supervisor review.",
  },
  documentation: {
    eyebrow: "Most popular",
    description: "Full chapters, diagrams, and edits for one complete project.",
    featured: true,
  },
  siwes: {
    eyebrow: "SIWES",
    description: "Standalone SIWES report — no existing project required.",
  },
};

const PricingSkeleton = () => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
    {[1, 2, 3, 4].map((i) => (
      <div
        key={i}
        className="h-80 animate-pulse rounded-2xl border border-slate-200/80 bg-white"
      />
    ))}
  </div>
);

const PricingCard = ({
  product,
  isActive,
  isCheckoutContext,
  loading,
  projectsLoading,
  projects,
  projectId,
  selectedProjectId,
  onSelectProject,
  onSelect,
  onPay,
  onCreateProject,
}) => {
  const meta = PRODUCT_META[product.key] || {};
  const isFeatured = meta.featured && !isCheckoutContext;
  const showCheckout = isCheckoutContext || isActive;

  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white transition-all ${
        showCheckout
          ? "border-[#0BBF64] shadow-lift ring-2 ring-[#0BBF64]/20"
          : isFeatured
            ? "border-emerald-200/80 shadow-soft"
            : "border-slate-200/80 shadow-sm hover:border-slate-300 hover:shadow-soft"
      }`}
    >
      {isFeatured && (
        <div className="absolute top-4 right-4">
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#0BBF64] ring-1 ring-emerald-200/60">
            Popular
          </span>
        </div>
      )}

      <div
        className={`h-1 ${
          isFeatured
            ? "bg-gradient-to-r from-[#0BBF64] via-emerald-400 to-teal-300"
            : "bg-slate-100"
        }`}
      />

      <div className="flex flex-1 flex-col p-6 sm:p-7">
        {meta.eyebrow && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0BBF64]">
            {meta.eyebrow}
          </p>
        )}
        <h2 className="mt-1 text-lg font-bold text-slate-900">{product.title}</h2>
        {meta.description && (
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {meta.description}
          </p>
        )}

        <div className="mt-5 flex items-baseline gap-1.5">
          <span className="text-3xl font-extrabold tracking-tight text-slate-900">
            {formatNaira(product.price)}
          </span>
          <span className="text-xs text-slate-400">one-time</span>
        </div>

        <ul className="mt-6 flex-1 space-y-3">
          {product.limits.map((text) => (
            <li
              key={text}
              className="flex items-start gap-3 text-sm text-slate-700"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[#0BBF64]">
                <CheckIcon />
              </span>
              {text}
            </li>
          ))}
        </ul>

        {product.needsProject && !projectId && showCheckout && (
          <div className="mt-5 rounded-xl border border-slate-200/80 bg-slate-50/80 p-4">
            {projectsLoading ? (
              <p className="text-sm text-slate-500 animate-soft-pulse">
                Loading projects…
              </p>
            ) : projects.length === 0 ? (
              <p className="text-sm text-slate-600">
                <button
                  type="button"
                  className="font-semibold text-[#0BBF64] hover:text-[#0aa459]"
                  onClick={onCreateProject}
                >
                  Create a project
                </button>{" "}
                first to continue.
              </p>
            ) : (
              <label className="block">
                <span className="text-xs font-medium text-slate-500">
                  Select project
                </span>
                <select
                  className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-[#0BBF64] focus:outline-none focus:ring-2 focus:ring-[#0BBF64]/20"
                  value={selectedProjectId || ""}
                  onChange={(e) => onSelectProject(e.target.value)}
                >
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <button
          type="button"
          disabled={showCheckout && loading}
          onClick={() => {
            if (!isCheckoutContext) onSelect(product.key);
            if (showCheckout) onPay(product);
          }}
          className={`mt-6 inline-flex w-full items-center justify-center rounded-xl px-6 py-3.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            showCheckout
              ? "bg-[#0BBF64] text-white shadow-lg shadow-emerald-500/20 hover:bg-[#0aa459] hover:shadow-emerald-500/30"
              : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          {showCheckout && loading
            ? "Redirecting…"
            : showCheckout
              ? "Pay now"
              : "Select plan"}
        </button>
      </div>
    </div>
  );
};

const Pricing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("projectId");
  const ideaPack = searchParams.get("ideaPack") === "1";
  const generationTypeParam = (searchParams.get("generationType") || "").toUpperCase();

  const [pricing, setPricing] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [activeProduct, setActiveProduct] = useState(
    ideaPack
      ? "idea"
      : generationTypeParam === "PROPOSAL"
        ? "proposal"
        : generationTypeParam === "SIWES_REPORT"
          ? "siwes"
          : generationTypeParam === "DOCUMENTATION"
            ? "documentation"
            : null
  );

  useEffect(() => {
    api
      .get("/pricing")
      .then((res) => setPricing(res?.data?.data || null))
      .catch(() => setPricing(null));
  }, []);

  useEffect(() => {
    if (ideaPack || generationTypeParam === "SIWES_REPORT") return;
    if (!projectId) {
      setProjectsLoading(true);
      projectsApi
        .list({})
        .then((res) => {
          const list = res?.data?.data?.projects || [];
          setProjects(list);
          if (list.length > 0 && !selectedProjectId) {
            setSelectedProjectId(list[0]._id);
          }
        })
        .catch(() => setProjects([]))
        .finally(() => setProjectsLoading(false));
    }
  }, [ideaPack, projectId, generationTypeParam]);

  const products = pricing
    ? [
        {
          key: "idea",
          title: "Idea generation pack",
          price: pricing.ideaGeneration.packFeeNgn,
          limits: [
            `${pricing.ideaGeneration.freeLimit} free generation runs included`,
            `${pricing.ideaGeneration.packSize} extra runs per pack`,
            "Course-aware topic ideas",
          ],
          productType: "IDEA_GENERATION_PACK",
          needsProject: false,
        },
        {
          key: "proposal",
          title: "Project proposal",
          price: pricing.proposal.feeNgn,
          limits: [
            `${pricing.proposal.regenerationsAllowed} full regenerations`,
            `${pricing.proposal.baseEdits} edits included`,
            "One complete proposal per project",
          ],
          productType: "PROPOSAL_GENERATION",
          needsProject: true,
          generationType: "PROPOSAL",
        },
        {
          key: "documentation",
          title: "Project documentation",
          price: pricing.documentation.feeNgn,
          limits: [
            `${pricing.documentation.regenerationsAllowed} full regenerations`,
            `${pricing.documentation.baseEdits} edits included`,
            "Mermaid diagram flows",
            "One complete documentation set per project",
          ],
          productType: "DOCUMENTATION_GENERATION",
          needsProject: true,
          generationType: "DOCUMENTATION",
        },
        {
          key: "siwes",
          title: "SIWES report",
          price: pricing.siwesReport.feeNgn,
          limits: [
            `${pricing.siwesReport.regenerationsAllowed} full regenerations`,
            `${pricing.siwesReport.baseEdits} edits included`,
            "Standalone — no project required",
            "Course template-aware output",
          ],
          productType: "SIWES_REPORT_GENERATION",
          needsProject: false,
          generationType: "SIWES_REPORT",
        },
      ]
    : [];

  const isCheckoutContext = !!(ideaPack || generationTypeParam || projectId);
  const visibleProducts =
    isCheckoutContext && activeProduct
      ? products.filter((p) => p.key === activeProduct)
      : products;

  const selectedProduct =
    products.find((p) => p.key === activeProduct) ||
    visibleProducts[0] ||
    products[0];
  const checkoutTitle = selectedProduct?.title || "Pricing";

  const handlePay = async (product) => {
    setLoading(true);
    setError("");
    try {
      const payload =
        product.key === "idea"
          ? { amount: product.price, productType: product.productType }
          : product.needsProject
            ? (() => {
                const payProjectId = selectedProjectId || projectId;
                if (!payProjectId) {
                  throw new Error(
                    "Please create a project first, then return here to pay."
                  );
                }
                return {
                  amount: product.price,
                  productType: product.productType,
                  projectId: payProjectId,
                };
              })()
            : { amount: product.price, productType: product.productType };

      const res = await api.post("/payments/initialize", payload);
      const link = res?.data?.data?.link;
      if (link) {
        window.location.href = link;
        return;
      }
      setError(res?.data?.message || "Could not start payment");
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Please log in first.");
        navigate("/login");
        return;
      }
      setError(
        e?.response?.data?.message || e?.message || "Payment failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1">
        {/* Header */}
        <section className="relative overflow-hidden border-b border-slate-200/60 bg-slate-50/50">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(11,191,100,0.1),transparent)]"
          />
          <div className="relative max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-16">
            <div className="max-w-2xl mx-auto text-center">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#0BBF64]">
                {isCheckoutContext ? "Checkout" : "Pricing"}
              </p>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                {isCheckoutContext && activeProduct ? checkoutTitle : "Simple, transparent pricing"}
              </h1>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-600">
                {isCheckoutContext && activeProduct
                  ? "Review your plan and complete payment securely."
                  : "Pay once per product. No subscriptions, no hidden fees — just clear limits on edits and regenerations."}
              </p>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="max-w-6xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
          {!pricing ? (
            <PricingSkeleton />
          ) : (
            <>
              {!isCheckoutContext && (
                <SectionHeader
                  eyebrow="Plans"
                  title="Choose what fits your stage."
                  description="Start with topic ideas, move to a proposal, or go straight to full documentation."
                  className="mb-10 text-center mx-auto"
                />
              )}

              <div
                className={`grid grid-cols-1 gap-5 ${
                  visibleProducts.length > 1
                    ? "md:grid-cols-2"
                    : "max-w-md mx-auto"
                }`}
              >
                {visibleProducts.map((product) => (
                  <PricingCard
                    key={product.key}
                    product={product}
                    isActive={activeProduct === product.key}
                    isCheckoutContext={isCheckoutContext}
                    loading={loading}
                    projectsLoading={projectsLoading}
                    projects={projects}
                    projectId={projectId}
                    selectedProjectId={selectedProjectId}
                    onSelectProject={setSelectedProjectId}
                    onSelect={setActiveProduct}
                    onPay={handlePay}
                    onCreateProject={() => navigate("/dashboard/project/new")}
                  />
                ))}
              </div>

              {error && (
                <div className="mt-6 mx-auto max-w-md rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </section>

        {/* Trust strip */}
        {!isCheckoutContext && pricing && (
          <section className="border-t border-slate-200/60 bg-slate-50/50">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
              <div className="grid gap-4 sm:grid-cols-3 text-center">
                {[
                  "Secure payment via Paystack",
                  "One-time fee per product",
                  "Edits & regenerations included",
                ].map((text) => (
                  <div
                    key={text}
                    className="flex items-center justify-center gap-2 text-sm text-slate-600"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-[#0BBF64]">
                      <CheckIcon />
                    </span>
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
